import { useAuth0 } from '@auth0/auth0-react'

import { HospitalAdditionStatusTypes } from 'models/HospitalAdditionStatus.model'
import { USStates } from 'models/USStates.model'
import React, { useEffect, useState } from 'react'

import { AgencyService } from '../services/AgencyService/AgencyService'
import { HospitalService } from '../services/HospitalService/HospitalService'
import UserService from '../services/UserService/UserService'
import { getApiErrorMessage } from '../utils/apiError'

type AddMissingModalProps = {
  isOpen: boolean
  onClose: () => void
  type: 'hospital' | 'agency'
}

function formatStateLabel(enumKey: string): string {
  return enumKey.replace(/([a-z])([A-Z])/g, '$1 $2')
}

export const AddMissingModal: React.FC<AddMissingModalProps> = ({ isOpen, onClose, type }) => {
  const { user, isAuthenticated } = useAuth0()
  const [formData, setFormData] = useState({
    additionalInfo: '',
    city: '',
    email: '',
    name: '',
    state: '' as USStates | '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !isAuthenticated || !user) return
    const formatted = UserService.formatUser(user)
    setFormData((prev) => ({
      ...prev,
      email: formatted.email || '',
    }))
  }, [isOpen, isAuthenticated, user])

  const resetForm = () => {
    setFormData({ additionalInfo: '', city: '', email: '', name: '', state: '' })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const submissionNotes = formData.additionalInfo.trim() || undefined

      if (type === 'hospital') {
        const hospitalService = new HospitalService()
        await hospitalService.add({
          city: formData.city,
          name: formData.name,
          newHospitalStatus: HospitalAdditionStatusTypes.PENDING, // Set status to pending for new submissions
          state: formData.state,
          submissionNotes,
        })
      } else {
        const agencyService = new AgencyService()
        await agencyService.add({
          address: `${formData.city}, ${formData.state}`,
          name: formData.name,
          newAgencyStatus: HospitalAdditionStatusTypes.PENDING, // Set status to pending for new submissions
          submissionNotes,
        })
      }

      setIsSubmitting(false)
      setSubmitSuccess(true)

      setTimeout(() => {
        onClose()
        setSubmitSuccess(false)
        resetForm()
      }, 2000)
    } catch (err) {
      console.error('Error submitting form:', err)
      setError(
        getApiErrorMessage(
          err,
          type === 'hospital'
            ? 'Sorry, there was a problem adding the hospital.'
            : 'Sorry, there was a problem adding the agency.',
        ),
      )
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    onClose()
    setSubmitSuccess(false)
    setError(null)
    resetForm()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-graphite-grey">
            Add Missing {type === 'hospital' ? 'Hospital' : 'Agency'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="text-green-500 text-5xl mb-4">✓</div>
              <h3 className="text-xl font-semibold text-graphite-grey mb-2">Thank you!</h3>
              <p className="text-gray-600">
                Your request has been submitted. We&apos;ll review and add the {type} soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>
              )}

              <div>
                <label className="block text-sm font-semibold text-graphite-grey mb-1">
                  {type === 'hospital' ? 'Hospital' : 'Agency'} Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent"
                  placeholder={`Enter ${type} name`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-graphite-grey mb-1">City *</label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-graphite-grey mb-1">State *</label>
                  <select
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value as USStates })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent bg-white"
                  >
                    <option value="" disabled>
                      Select state
                    </option>
                    {Object.entries(USStates).map(([key, code]) => (
                      <option key={code} value={code}>
                        {formatStateLabel(key)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-graphite-grey mb-1">Your Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  placeholder="your@email.com"
                />
                {formData.email ? <p className="text-xs text-gray-500 mt-1">✓ Pre-filled from your profile</p> : null}
              </div>

              <div>
                <label className="block text-sm font-semibold text-graphite-grey mb-1">Additional Information</label>
                <textarea
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent resize-none"
                  placeholder="Any additional details (address, website, etc.)"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-orange text-white rounded-lg font-semibold hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
